import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  SimpleGrid,
  Stack,
  Text,
  Link,
  Heading,
  Divider,
  Icon,
  HStack,
  IconButton,
  Image,
  Button,
} from '@chakra-ui/react';
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaInstagram,
  FaHeart,
  FaWhatsapp,
} from 'react-icons/fa';
import logoImage from '../Components/Assets/logo-mark.png';

const quickLinks = [
  { name: 'Accueil', path: '/' },
  { name: 'À Propos', path: '/about' },
  { name: 'Agro Business', path: '/agro-business' },
  { name: 'Industries', path: '/industries' },
  { name: 'Contact', path: '/contact' },
];

const services = [
  { name: 'Cacao & Macadamia', path: '/agro-business' },
  { name: 'Clouterie', path: '/industries' },
  { name: 'Sécurité Alimentaire', path: '/agro-business' },
  { name: 'Fournitures Bâtiment', path: '/industries' },
];

const socialLinks = [
  { icon: FaFacebook, label: 'Facebook', href: '#' },
  { icon: FaTwitter, label: 'Twitter', href: '#' },
  { icon: FaLinkedin, label: 'LinkedIn', href: '#' },
  { icon: FaInstagram, label: 'Instagram', href: '#' },
];

const FooterTitle = ({ children }) => (
  <Heading
    as="h4"
    size="xs"
    color="white"
    textTransform="uppercase"
    letterSpacing="0.16em"
    fontWeight="700"
  >
    {children}
  </Heading>
);

const FooterLink = ({ as, to, children }) => (
  <Link
    as={as}
    to={to}
    fontSize="sm"
    color="gray.400"
    transition="all 0.2s"
    display="inline-flex"
    alignItems="center"
    gap={2}
    minH={{ base: '40px', md: 'auto' }}
    _hover={{ color: 'white', transform: 'translateX(4px)' }}
    _before={{
      content: '""',
      w: '14px',
      h: '1px',
      bg: 'brand.500',
      opacity: 0.7,
    }}
  >
    {children}
  </Link>
);

function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      bg="navy.900"
      color="gray.300"
      pt={{ base: 14, md: 20 }}
      pb={0}
      position="relative"
      overflow="hidden"
    >
      {/* Decorative layers */}
      <Box position="absolute" inset={0} className="bg-grid" opacity={0.4} pointerEvents="none" />
      <Box
        position="absolute"
        top="-160px"
        right="-120px"
        w="440px"
        h="440px"
        borderRadius="full"
        bgGradient="radial(circle at center, rgba(20,138,85,0.32), transparent 70%)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-200px"
        left="-140px"
        w="440px"
        h="440px"
        borderRadius="full"
        bgGradient="radial(circle at center, rgba(201,147,31,0.16), transparent 70%)"
        pointerEvents="none"
      />

      <Container maxW="7xl" position="relative">
        {/* ─── CTA STRIP ─── */}
        <Box
          display={{ base: 'none', lg: 'flex' }}
          alignItems="center"
          justifyContent="space-between"
          gap={8}
          mb={16}
          px={8}
          py={6}
          borderRadius="2xl"
          bg="whiteAlpha.100"
          border="1px solid"
          borderColor="whiteAlpha.200"
          backdropFilter="blur(8px)"
        >
          <Box>
            <Text fontWeight="700" color="white" fontSize="lg">
              Un projet, un devis, une question ?
            </Text>
            <Text fontSize="sm" color="gray.400">
              Notre équipe vous répond en moins de 24 heures, du lundi au samedi.
            </Text>
          </Box>
          <HStack spacing={3}>
            <Button as={RouterLink} to="/contact" size="lg" bg="brand.500" color="white">
              Nous contacter
            </Button>
            <Button
              as="a"
              href="https://wa.me/243810330830?text=Bonjour%20TRIMER%20SARL%20!"
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
              variant="ghostOnDark"
              leftIcon={<FaWhatsapp />}
            >
              WhatsApp
            </Button>
          </HStack>
        </Box>

        {/* ─── MAIN FOOTER CONTENT ─── */}
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={{ base: 10, lg: 12 }} mb={14}>
          {/* ── Brand ── */}
          <Stack spacing={5}>
            <HStack spacing={3}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                h="48px"
                w="48px"
                borderRadius="xl"
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.200"
                flexShrink={0}
              >
                <Image src={logoImage} alt="TRIMER SARL" h="34px" w="auto" objectFit="contain" />
              </Box>
              <Heading as="h3" size="lg" color="white" letterSpacing="tight">
                TRIMER{' '}
                <Text as="span" color="gold.300">
                  SARL
                </Text>
              </Heading>
            </HStack>

            <Text fontSize="sm" lineHeight="1.9" color="gray.400">
              Entreprise multisectorielle — fer de lance de la synergie entre le
              développement agricole et l'essor industriel en RDC.
            </Text>

            <Box
              px={4}
              py={3}
              borderRadius="xl"
              bg="whiteAlpha.100"
              borderLeft="3px solid"
              borderColor="gold.400"
            >
              <Text fontSize="sm" fontStyle="italic" color="whiteAlpha.800">
                « De la ferme à la quincaillerie »
              </Text>
            </Box>

            <HStack spacing={2}>
              {socialLinks.map((social) => (
                <IconButton
                  key={social.label}
                  as="a"
                  href={social.href}
                  aria-label={social.label}
                  icon={<Icon as={social.icon} />}
                  size="md"
                  variant="ghost"
                  color="gray.400"
                  borderRadius="full"
                  bg="whiteAlpha.100"
                  _hover={{ color: 'white', bg: 'brand.500', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                />
              ))}
            </HStack>
          </Stack>

          {/* ── Quick Links ── */}
          <Stack spacing={5}>
            <FooterTitle>Liens Rapides</FooterTitle>
            <Stack spacing={3}>
              {quickLinks.map((link) => (
                <FooterLink key={link.name} as={RouterLink} to={link.path}>
                  {link.name}
                </FooterLink>
              ))}
            </Stack>
          </Stack>

          {/* ── Products ── */}
          <Stack spacing={5}>
            <FooterTitle>Nos Produits</FooterTitle>
            <Stack spacing={3}>
              {services.map((service) => (
                <FooterLink key={service.name} as={RouterLink} to={service.path}>
                  {service.name}
                </FooterLink>
              ))}
            </Stack>
          </Stack>

          {/* ── Contact ── */}
          <Stack spacing={5}>
            <FooterTitle>Contact</FooterTitle>
            <Stack spacing={4} fontSize="sm" color="gray.400">
              <HStack align="start" spacing={3}>
                <Icon as={FaMapMarkerAlt} color="brand.300" mt={1} />
                <Box>
                  <Text color="whiteAlpha.900" fontWeight="600">Adresse</Text>
                  <Text>Bunia, Province de l'Ituri</Text>
                  <Text>République Démocratique du Congo</Text>
                </Box>
              </HStack>

              <HStack align="start" spacing={3}>
                <Icon as={FaPhone} color="brand.300" mt={1} />
                <Box>
                  <Text color="whiteAlpha.900" fontWeight="600">Téléphones</Text>
                  <Link href="tel:+243836944316" _hover={{ color: 'white' }} transition="0.2s" display="block">
                    +243 836 944 316
                  </Link>
                  <Link href="tel:+243984270966" _hover={{ color: 'white' }} transition="0.2s" display="block">
                    +243 984 270 966
                  </Link>
                  <Link href="tel:+243844088564" _hover={{ color: 'white' }} transition="0.2s" display="block">
                    +243 844 088 564
                  </Link>
                </Box>
              </HStack>

              <HStack align="start" spacing={3}>
                <Icon as={FaEnvelope} color="brand.300" mt={1} />
                <Box>
                  <Text color="whiteAlpha.900" fontWeight="600">Email</Text>
                  <Link href="mailto:trimersarl7@gmail.com" _hover={{ color: 'white' }} transition="0.2s">
                    trimersarl7@gmail.com
                  </Link>
                </Box>
              </HStack>

              <HStack align="start" spacing={3}>
                <Icon as={FaMapMarkerAlt} color="gold.300" mt={1} />
                <Box>
                  <Text color="whiteAlpha.900" fontWeight="600">Sites de Production</Text>
                  <Text fontSize="xs">Tshopo : Bafwasende</Text>
                  <Text fontSize="xs">Ituri : Irumu (Komanda)</Text>
                  <Text fontSize="xs">Ituri : Irumu (Tsere)</Text>
                </Box>
              </HStack>
            </Stack>
          </Stack>
        </SimpleGrid>

        <Divider borderColor="whiteAlpha.200" />

        {/* ─── BOTTOM BAR ─── */}
        <HStack
          justify="space-between"
          flexWrap="wrap"
          py={6}
          spacing={4}
          fontSize="sm"
          color="gray.500"
        >
          <Text>&copy; {currentYear} TRIMER SARL. Tous droits réservés.</Text>

          <HStack fontSize="sm" spacing={1}>
            <Text>Fait avec</Text>
            <Icon as={FaHeart} color="red.400" />
            <Text>en RDC</Text>
          </HStack>

          <HStack fontSize="sm" spacing={4}>
            <Link href="#" color="gray.500" _hover={{ color: 'white' }} transition="0.2s">
              Confidentialité
            </Link>
            <Link href="#" color="gray.500" _hover={{ color: 'white' }} transition="0.2s">
              Conditions d'Utilisation
            </Link>
          </HStack>
        </HStack>
      </Container>
    </Box>
  );
}

export default Footer;
